import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  message,
  Row,
  Col,
  Table,
  Card,
  Divider,
  Typography
} from 'antd';
import {
  SaveOutlined,
  SendOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import paymentService, { Payment, CreatePaymentInput } from '../../../../services/paymentService';
import invoiceService, { Invoice } from '../../../../services/invoiceService';
import apiService from '../../../../services/apiService';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

// Payment Method Options
const PAYMENT_METHODS = [
  { id: 'CASH', label: 'Cash', icon: '💵' },
  { id: 'BANK_TRANSFER', label: 'Bank Transfer', icon: '🏦' },
  { id: 'CREDIT_CARD', label: 'Credit Card', icon: '💳' },
  { id: 'DEBIT_CARD', label: 'Debit Card', icon: '💳' },
  { id: 'CHECK', label: 'Check', icon: '📝' },
  { id: 'E_WALLET', label: 'E-Wallet', icon: '📱' },
  { id: 'WIRE_TRANSFER', label: 'Wire Transfer', icon: '🌐' },
  { id: 'OTHER', label: 'Other', icon: '📋' }
];

interface PaymentFormProps {
  visible: boolean;
  payment?: Payment | null;
  invoice?: Invoice | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface PaymentAllocationRow {
  key: string;
  invoiceId?: string;
  invoiceNo?: string;
  invoiceAmount?: number;
  amountAllocated: number;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ visible, payment, invoice, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [paymentType, setPaymentType] = useState<'INBOUND' | 'OUTBOUND'>('INBOUND');
  const [partnerType, setPartnerType] = useState<'CUSTOMER' | 'VENDOR'>('CUSTOMER');
  const [customers, setCustomers] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [journals, setJournals] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]); // Chart of Accounts for destination account
  const [allocations, setAllocations] = useState<PaymentAllocationRow[]>([]);
  const [totalAllocated, setTotalAllocated] = useState(0);

  useEffect(() => {
    const initializeForm = async () => {
      if (visible) {
        // Load all data first and capture the returned values
        const [loadedCustomers, , loadedJournals, loadedAccounts] = await Promise.all([
          loadCustomers(),
          loadVendors(),
          loadJournals(),
          loadAccounts()
        ]);

        if (payment) {
          // Edit mode
          form.setFieldsValue({
            paymentType: payment.paymentType,
            partnerType: payment.partnerType,
            partnerId: payment.partnerId,
            amount: payment.amount,
            paymentDate: dayjs(payment.paymentDate),
            paymentMethodId: payment.paymentMethodId,
            journalId: payment.journalId,
            accountId: payment.accountId,
            memo: payment.memo
          });
          setPaymentType(payment.paymentType);
          setPartnerType(payment.partnerType);

          // Load allocations
          if (payment.invoiceAllocations && payment.invoiceAllocations.length > 0) {
            const allocs: PaymentAllocationRow[] = payment.invoiceAllocations.map((alloc, index) => ({
              key: `alloc-${index}`,
              invoiceId: alloc.invoiceId,
              amountAllocated: alloc.amountAllocated
            }));
            setAllocations(allocs);
            calculateTotalAllocated(allocs);
          }
        } else if (invoice) {
          // Create payment from invoice - use loaded customers data
          let customersData = loadedCustomers;

          // Ensure the invoice's customer is in the customers list
          if (invoice.customer) {
            const customerExists = customersData.some((c: any) => c.id === invoice.customer!.id);
            if (!customerExists) {
              customersData = [...customersData, invoice.customer];
              setCustomers(customersData);
            }
          }

          const customerId = invoice.customer?.id || invoice.customerId;

          // Pre-fill Journal and Destination Account. They are required to post,
          // and leaving them blank was the practical failure: the user pressed
          // "Create & Post", antd rejected validation, and the catch surfaced
          // `error.message` — undefined for a validation rejection — so it looked
          // like nothing happened at all.
          //
          // Destination defaults to the "Outstanding Receipts" clearing account
          // (money received but not yet reconciled to a specific bank), which is
          // how the source Odoo ledger modelled it. The user can change it.
          const defaultAccount =
            (loadedAccounts || [])
              .filter((a: any) => !a.deprecated && /outstanding\s*receipts/i.test(a.name || ''))
              .sort((a: any, b: any) => String(a.code).localeCompare(String(b.code)))[0]
            || (loadedAccounts || []).find((a: any) => /bank|cash/i.test(a.name || ''));

          const defaultJournal =
            (loadedJournals || []).find((j: any) => j.code === 'BNK1')
            || (loadedJournals || []).find((j: any) => j.type === 'BANK')
            || (loadedJournals || []).find((j: any) => j.type === 'CASH');

          form.setFieldsValue({
            paymentType: 'INBOUND',
            partnerType: 'CUSTOMER',
            partnerId: customerId,
            amount: invoice.totalAmount,
            paymentDate: dayjs(),
            paymentMethodId: 'BANK_TRANSFER', // Default to bank transfer for invoice payments
            journalId: defaultJournal?.id,
            accountId: defaultAccount?.id,
            memo: `Payment for Invoice ${invoice.invoiceNo}`
          });
          setPaymentType('INBOUND');
          setPartnerType('CUSTOMER');

          // Load customer invoices
          await loadCustomerInvoices(customerId);

          // Auto-allocate to this invoice
          const initialAllocation: PaymentAllocationRow = {
            key: `alloc-${Date.now()}`,
            invoiceId: invoice.id,
            invoiceNo: invoice.invoiceNo,
            invoiceAmount: Number(invoice.totalAmount),
            amountAllocated: Number(invoice.totalAmount)
          };
          setAllocations([initialAllocation]);
          calculateTotalAllocated([initialAllocation]);
        } else {
          // Create mode
          form.resetFields();
          setAllocations([]);
          setTotalAllocated(0);
          setPaymentType('INBOUND');
          setPartnerType('CUSTOMER');
        }
      }
    };

    initializeForm();
  }, [visible, payment, invoice, form]);

  const loadCustomers = async () => {
    try {
      // Use lightweight dropdown endpoint (returns only id, name, email, phone)
      const response: any = await apiService.get('/customers/dropdown');
      const data = response.customers || [];
      setCustomers(data);
      return data;
    } catch (error) {
      console.error('Failed to load customers:', error);
      message.error('Failed to load customers');
      return [];
    }
  };

  const loadVendors = async () => {
    try {
      const response: any = await apiService.get('/accounting/vendors');
      const data = response.vendors || response.data || [];
      setVendors(data);
      return data;
    } catch (error) {
      console.error('Failed to load vendors:', error);
      return [];
    }
  };

  const loadJournals = async () => {
    try {
      const response: any = await apiService.get('/accounting/journals');
      const data = response.data || [];
      setJournals(data);
      return data;
    } catch (error) {
      console.error('Failed to load journals:', error);
      return [];
    }
  };

  const loadAccounts = async () => {
    try {
      const response: any = await apiService.get('/accounting/accounts');
      const data = response.data || [];
      // Filter to show only ASSET type accounts (Cash, Bank accounts)
      const assetAccounts = data.filter((account: any) => account.accountType === 'ASSET');
      setAccounts(assetAccounts);
      return assetAccounts;
    } catch (error) {
      console.error('Failed to load accounts:', error);
      return [];
    }
  };

  const loadCustomerInvoices = async (customerId: string) => {
    try {
      const data = await invoiceService.getInvoicesByCustomer(customerId);
      // Unpaid = anything already posted to the ledger but not yet settled.
      // CONFIRM belongs here: confirming is what creates the journal entry, so
      // the receivable exists and can be paid even if the invoice was never
      // emailed. Omitting it meant a confirmed invoice could not be selected in
      // "Allocate to Invoices" at all.
      const unpaidInvoices = data.filter(
        inv => inv.status === 'SENT' || inv.status === 'CONFIRM' || inv.status === 'OVERDUE'
      );
      setInvoices(unpaidInvoices);
    } catch (error) {
      console.error('Failed to load invoices:', error);
    }
  };

  const handlePartnerChange = (partnerId: string) => {
    if (partnerType === 'CUSTOMER') {
      loadCustomerInvoices(partnerId);
    }
  };

  const addAllocation = () => {
    const newAllocation: PaymentAllocationRow = {
      key: `alloc-${Date.now()}`,
      amountAllocated: 0
    };
    const newAllocations = [...allocations, newAllocation];
    setAllocations(newAllocations);
  };

  const updateAllocation = (key: string, field: keyof PaymentAllocationRow, value: any) => {
    const newAllocations = allocations.map(alloc => {
      if (alloc.key === key) {
        return { ...alloc, [field]: value };
      }
      return alloc;
    });
    setAllocations(newAllocations);
    calculateTotalAllocated(newAllocations);
  };

  const removeAllocation = (key: string) => {
    const newAllocations = allocations.filter(alloc => alloc.key !== key);
    setAllocations(newAllocations);
    calculateTotalAllocated(newAllocations);
  };

  const calculateTotalAllocated = (allocs: PaymentAllocationRow[]) => {
    const total = allocs.reduce((sum, alloc) => sum + (alloc.amountAllocated || 0), 0);
    setTotalAllocated(total);
  };

  const handleSubmit = async (isDraft: boolean = true) => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const paymentData: CreatePaymentInput = {
        paymentType: values.paymentType,
        partnerType: values.partnerType,
        partnerId: values.partnerId,
        amount: values.amount,
        paymentDate: values.paymentDate.format('YYYY-MM-DD'),
        paymentMethodId: values.paymentMethodId,
        journalId: values.journalId,
        accountId: values.accountId, // Chart of Account - destination account
        memo: values.memo,
        state: 'DRAFT',  // Always create as DRAFT first
        allocations: allocations
          .filter(alloc => alloc.invoiceId && alloc.amountAllocated > 0)
          .map(alloc => ({
            invoiceId: alloc.invoiceId,
            amountAllocated: alloc.amountAllocated
          }))
      };

      if (payment) {
        // Update existing payment
        await paymentService.updatePayment(payment.id, paymentData);
        message.success('Payment updated successfully');
      } else {
        // Create new payment
        const newPayment = await paymentService.createPayment(paymentData);

        // If "Create & Post", post the payment to create journal entry
        if (!isDraft) {
          // Resolve AR / AP / cash accounts. Prefer the company's configured
          // defaults from accounting_settings; fall back to a strict code
          // allowlist (the legacy code.includes('1200') heuristic missed the
          // current 124000 AR account on every tenant that migrated, which
          // silently failed the post and left the invoice on SENT).
          const settingsResp: any = await apiService.get('/accounting/settings');
          const settings = settingsResp?.data || {};

          // Need the FULL chart (not just ASSET) so we can find AR / AP.
          const accountsResp: any = await apiService.get('/accounting/accounts');
          const allAccounts: any[] = accountsResp?.data || [];

          const findById = (id?: string | null) => id ? allAccounts.find(a => a.id === id) : null;

          const arAccount =
            findById(settings.defaultReceivableAccountId) ||
            allAccounts.find(a => a.code === '124000') ||
            allAccounts.find(a => a.code?.startsWith('1200'));

          const apAccount =
            findById(settings.defaultPayableAccountId) ||
            allAccounts.find(a => a.code === '210000') ||
            allAccounts.find(a => a.code?.startsWith('2100')) ||
            allAccounts.find(a => a.code?.startsWith('2000'));

          // For cash: the payment.accountId the user picked in the form is the
          // authoritative destination (the backend uses payment.accountId first
          // and only falls back to cashAccountId). So we just need *something*
          // sane in the fallback slot.
          const cashAccount =
            findById(values.accountId) ||
            findById(settings.defaultBankAccountId) ||
            allAccounts.find(a => a.code === '110000') ||
            allAccounts.find(a => a.code?.startsWith('110') && a.accountType === 'ASSET');

          if (!arAccount || !apAccount || !cashAccount) {
            const missing: string[] = [];
            if (!arAccount)   missing.push('Accounts Receivable (default in Settings, or code 124000 / 1200xxx)');
            if (!apAccount)   missing.push('Accounts Payable (default in Settings, or code 210000 / 2100xxx / 2000xxx)');
            if (!cashAccount) missing.push('Cash / Bank (the destination account on the form, or default Bank in Settings)');
            message.error(`Missing accounts for posting: ${missing.join(' • ')}`);
            setLoading(false);
            return;
          }

          try {
            await paymentService.postPayment(newPayment.id, {
              cashAccountId: cashAccount.id,
              arAccountId: arAccount.id,
              apAccountId: apAccount.id,
              journalId: values.journalId
            });
            message.success('Payment created and posted successfully');
          } catch (error: any) {
            message.error(`Payment created but failed to post: ${error.message}`);
          }
        } else {
          message.success('Payment saved as draft successfully');
        }
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      // antd's validateFields rejects with { errorFields }, not an Error — so
      // error.message is undefined and the toast read "Failed to save payment:
      // undefined", which users reported as "nothing happened". Name the fields
      // that are actually missing instead.
      if (error?.errorFields?.length) {
        const fields = error.errorFields
          .map((f: any) => f.errors?.[0] || f.name?.join('.'))
          .filter(Boolean)
          .join(' • ');
        message.error(`Please complete the required fields: ${fields}`);
      } else {
        message.error(`Failed to save payment: ${error?.message || 'unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const allocationColumns: ColumnsType<PaymentAllocationRow> = [
    {
      title: 'Invoice',
      dataIndex: 'invoiceId',
      key: 'invoiceId',
      width: '50%',
      render: (value, record) => (
        <Select
          value={value}
          placeholder="Select invoice"
          onChange={(val) => {
            updateAllocation(record.key, 'invoiceId', val);
            const selectedInvoice = invoices.find(inv => inv.id === val);
            if (selectedInvoice) {
              updateAllocation(record.key, 'invoiceNo', selectedInvoice.invoiceNo);
              updateAllocation(record.key, 'invoiceAmount', selectedInvoice.totalAmount);
            }
          }}
          style={{ width: '100%' }}
        >
          {invoices.map(invoice => (
            <Option key={invoice.id} value={invoice.id}>
              {invoice.invoiceNo} - ${invoice.totalAmount}
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: 'Amount to Allocate',
      dataIndex: 'amountAllocated',
      key: 'amountAllocated',
      width: '35%',
      render: (value, record) => (
        <InputNumber
          min={0}
          max={record.invoiceAmount || 999999}
          precision={2}
          prefix="$"
          value={value}
          onChange={(val) => updateAllocation(record.key, 'amountAllocated', val || 0)}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: '',
      key: 'action',
      width: '15%',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeAllocation(record.key)}
        />
      )
    }
  ];

  return (
    <Modal
      title={payment ? 'Edit Payment' : 'Create New Payment'}
      open={visible}
      onCancel={onClose}
      width={900}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
      >
        {/* Display Invoice Numbers if allocated */}
        {allocations.length > 0 && allocations.some(a => a.invoiceNo) && (
          <Form.Item label="Invoice Number(s)">
            <Input
              value={allocations.map(a => a.invoiceNo).filter(Boolean).join(', ')}
              disabled
              style={{ fontWeight: 500, color: '#000' }}
            />
          </Form.Item>
        )}

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="paymentType"
              label="Payment Type"
              rules={[{ required: true }]}
            >
              <Select onChange={(value) => setPaymentType(value)}>
                <Option value="INBOUND">Inbound (Receive)</Option>
                <Option value="OUTBOUND">Outbound (Pay)</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="partnerType"
              label="Partner Type"
              rules={[{ required: true }]}
            >
              <Select onChange={(value) => setPartnerType(value)}>
                <Option value="CUSTOMER">Customer</Option>
                <Option value="VENDOR">Vendor</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="partnerId"
              label={partnerType === 'CUSTOMER' ? 'Customer' : 'Vendor'}
              rules={[{ required: true, message: 'Please select a partner' }]}
            >
              <Select
                showSearch
                placeholder={`Select a ${partnerType.toLowerCase()}`}
                optionFilterProp="children"
                onChange={handlePartnerChange}
              >
                {(partnerType === 'CUSTOMER' ? customers : vendors).map(partner => (
                  <Option key={partner.id} value={partner.id}>
                    {partner.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="amount"
              label="Payment Amount"
              rules={[{ required: true, message: 'Please enter amount' }]}
            >
              <InputNumber
                min={0}
                precision={2}
                prefix="$"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="paymentDate"
              label="Payment Date"
              rules={[{ required: true }]}
            >
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="paymentMethodId"
              label="Payment Method"
              rules={[{ required: true, message: 'Please select payment method' }]}
            >
              <Select placeholder="Select payment method">
                {PAYMENT_METHODS.map(method => (
                  <Option key={method.id} value={method.id}>
                    <span style={{ marginRight: 8 }}>{method.icon}</span>
                    {method.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="journalId"
              label="Journal"
              rules={[{ required: true }]}
            >
              <Select placeholder="Select journal">
                {journals.map(journal => (
                  <Option key={journal.id} value={journal.id}>
                    {journal.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="accountId"
              label="Destination Account"
              tooltip="The cash/bank account this payment goes into"
              rules={[{ required: true, message: 'Please select destination account' }]}
            >
              <Select
                placeholder="Select account"
                showSearch
                optionFilterProp="children"
              >
                {accounts.map(account => (
                  <Option key={account.id} value={account.id}>
                    {account.code} - {account.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="memo"
              label="Memo"
            >
              <TextArea rows={1} placeholder="Optional memo" />
            </Form.Item>
          </Col>
        </Row>

        {paymentType === 'INBOUND' && partnerType === 'CUSTOMER' && (
          <>
            <Divider>Allocate to Invoices</Divider>
            {invoices.length === 0 ? (
              <Card size="small" style={{ marginBottom: 16, textAlign: 'center', padding: 16 }}>
                <Text type="secondary">
                  {form.getFieldValue('partnerId')
                    ? 'This customer has no unpaid invoices available for allocation.'
                    : 'Please select a customer to see available invoices.'}
                </Text>
              </Card>
            ) : (
              <>
                <Table
                  columns={allocationColumns}
                  dataSource={allocations}
                  pagination={false}
                  size="small"
                  style={{ marginBottom: 16 }}
                  footer={() => (
                    <Button
                      type="dashed"
                      onClick={addAllocation}
                      icon={<PlusOutlined />}
                      block
                    >
                      Add Allocation
                    </Button>
                  )}
                />
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Row justify="end">
                    <Col>
                      Total Allocated: ${totalAllocated.toFixed(2)}
                    </Col>
                  </Row>
                </Card>
              </>
            )}
          </>
        )}

        <Row justify="end" gutter={8}>
          <Col>
            <Button onClick={onClose}>Cancel</Button>
          </Col>
          <Col>
            <Button
              onClick={() => handleSubmit(true)}
              loading={loading}
              icon={<SaveOutlined />}
            >
              Save as Draft
            </Button>
          </Col>
          {!payment && (
            <Col>
              <Button
                type="primary"
                onClick={() => handleSubmit(false)}
                loading={loading}
                icon={<SendOutlined />}
              >
                Create & Post
              </Button>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export default PaymentForm;